import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Download, Save, Receipt, Eye, EyeOff, RefreshCw, Languages, Mail, Printer, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import ClientPicker from '../components/ClientPicker';
import AutoTextarea from '../components/ui/AutoTextarea';
import { SortableList } from '../components/SortableList';
import DatePickerButton from '../components/DatePickerButton';
import PaymentsEditor from '../components/PaymentsEditor';
import ContingencyLibraryDialog from '../components/estimates/ContingencyLibraryDialog';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavGuard } from '../contexts/NavGuardContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorLang, setEditorLang] = useState('en');  // 'en' | 'fr'
  const [translating, setTranslating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showLibrary, setShowLibrary] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const { registerGuard } = useNavGuard();
  const leaveResolverRef = useRef(null);

  // Initialize all sections as expanded once invoice loads
  useEffect(() => {
    if (invoice && Object.keys(expandedSections).length === 0) {
      const exp = {};
      (invoice.sections || []).forEach((_, i) => { exp[i] = true; });
      setExpandedSections(exp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.invoice_id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/invoices/${id}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          // Legacy clients only have a combined `name` — split it so the
          // First/Last fields (matching the estimate editor) populate.
          const c = data.client || {};
          if (c.name && !c.first_name && !c.last_name) {
            const parts = c.name.trim().split(' ');
            data.client = {
              ...c,
              first_name: parts[0] || '',
              last_name: parts.slice(1).join(' ') || '',
            };
          }
          setInvoice(data);
        }
        else navigate('/invoices');
      } catch { navigate('/invoices'); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  // Language-aware field accessors. When editing in FR mode, all writes
  // go to the *_fr mirror fields so the EN master stays untouched until
  // the user explicitly back-translates.
  const sectionsKey = editorLang === 'fr' ? 'sections_fr' : 'sections';
  const titleKey = editorLang === 'fr' ? 'title_fr' : 'title';
  const termsKey = editorLang === 'fr' ? 'payment_terms_fr' : 'payment_terms';
  const notesKey = editorLang === 'fr' ? 'notes_fr' : 'notes';
  const bulletsKey = editorLang === 'fr' ? 'project_bullets_fr' : 'project_bullets';
  const provisionsKey = editorLang === 'fr' ? 'general_provisions_fr' : 'general_provisions';
  // When the user toggles to FR but no FR content exists yet, fall back
  // to the EN content so the form isn't blank — they can sync to populate.
  const currentSections = (invoice?.[sectionsKey]?.length ? invoice[sectionsKey] : (invoice?.sections || []));
  const currentTitle = invoice?.[titleKey] || (editorLang === 'fr' ? '' : invoice?.title || '');
  const currentTerms = invoice?.[termsKey] || (editorLang === 'fr' ? '' : invoice?.payment_terms || '');
  const currentNotes = invoice?.[notesKey] || (editorLang === 'fr' ? '' : invoice?.notes || '');
  const currentBullets = invoice?.[bulletsKey]?.length
    ? invoice[bulletsKey]
    : (invoice?.project_bullets || []);
  const currentProvisions = invoice?.[provisionsKey]?.length
    ? invoice[provisionsKey]
    : (invoice?.general_provisions || []);

  const updateField = (field, value) => {
    setInvoice(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const updateLangField = (baseField, value) => {
    // Writes to the language-appropriate mirror (`title` vs `title_fr`).
    const key = editorLang === 'fr' ? `${baseField}_fr` : baseField;
    setInvoice(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const updateClient = (field, value) => {
    setInvoice(prev => ({ ...prev, client: { ...(prev.client || {}), [field]: value } }));
    setHasUnsavedChanges(true);
  };

  const updateItem = (sIdx, iIdx, field, value) => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...(sections[sIdx].items || [])];
      items[iIdx] = { ...items[iIdx], [field]: value };
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const updateSectionTitle = (sIdx, value) => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], title: value };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const addItem = (sIdx) => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = [...(sections[sIdx].items || []), { description: '', qty: 1, unit_price: 0 }];
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const removeItem = (sIdx, iIdx) => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      const items = (sections[sIdx].items || []).filter((_, i) => i !== iIdx);
      sections[sIdx] = { ...sections[sIdx], items };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const addSection = () => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections.push({ title: editorLang === 'fr' ? 'Nouvelle section' : 'New Section', items: [], contingencies: [] });
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  const removeSection = (sIdx) => {
    setInvoice(prev => {
      const sections = (prev[sectionsKey] || prev.sections || []).filter((_, i) => i !== sIdx);
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };

  // Project bullets / scope points
  const addBullet = () => {
    setInvoice(prev => ({ ...prev, [bulletsKey]: [...(prev[bulletsKey] || []), ''] }));
    setHasUnsavedChanges(true);
  };
  const updateBullet = (idx, value) => {
    setInvoice(prev => {
      const bullets = [...(prev[bulletsKey] || [])];
      bullets[idx] = value;
      return { ...prev, [bulletsKey]: bullets };
    });
    setHasUnsavedChanges(true);
  };
  const removeBullet = (idx) => {
    setInvoice(prev => ({
      ...prev,
      [bulletsKey]: (prev[bulletsKey] || []).filter((_, i) => i !== idx),
    }));
    setHasUnsavedChanges(true);
  };

  // Section-level notes — individual sortable rows, same as the estimate
  // editor. Each note renders as its own bullet on the PDF.
  const updateSectionConts = (sIdx, conts) => {
    setInvoice(prev => {
      const sections = [...(prev[sectionsKey] || prev.sections || [])];
      sections[sIdx] = { ...sections[sIdx], contingencies: conts };
      return { ...prev, [sectionsKey]: sections };
    });
    setHasUnsavedChanges(true);
  };
  const addContingency = (sIdx) => {
    const sec = (currentSections || [])[sIdx] || {};
    updateSectionConts(sIdx, [...(sec.contingencies || []), '']);
  };
  const updateContingency = (sIdx, cIdx, value) => {
    const sec = (currentSections || [])[sIdx] || {};
    const conts = [...(sec.contingencies || [])];
    conts[cIdx] = value;
    updateSectionConts(sIdx, conts);
  };
  const removeContingency = (sIdx, cIdx) => {
    const sec = (currentSections || [])[sIdx] || {};
    updateSectionConts(sIdx, (sec.contingencies || []).filter((_, i) => i !== cIdx));
  };

  const handleSave = useCallback(async () => {
    if (!invoice) return false;
    setSaving(true);
    try {
      const res = await fetch(`${API}/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: invoice.title,
          client: invoice.client,
          sections: invoice.sections,
          project_bullets: invoice.project_bullets,
          general_provisions: invoice.general_provisions,
          status: invoice.status,
          tax_gst: invoice.tax_gst,
          tax_qst: invoice.tax_qst,
          deposit_paid: invoice.deposit_paid,
          payments: invoice.payments,
          payment_terms: invoice.payment_terms,
          notes: invoice.notes,
          // French mirrors — included so edits in FR view persist
          title_fr: invoice.title_fr,
          sections_fr: invoice.sections_fr,
          project_bullets_fr: invoice.project_bullets_fr,
          general_provisions_fr: invoice.general_provisions_fr,
          payment_terms_fr: invoice.payment_terms_fr,
          notes_fr: invoice.notes_fr,
        }),
      });
      if (res.ok) {
        toast.success('Saved');
        setHasUnsavedChanges(false);
        return true;
      }
      toast.error('Failed to save');
      return false;
    } catch { toast.error('Failed to save'); return false; }
    finally { setSaving(false); }
  }, [invoice, id]);

  const handleExportPdf = async () => {
    await handleSave();
    try {
      const res = await fetch(`${API}/invoices/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (!res.ok) { toast.error('Failed to export PDF'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
  };

  const handlePrintPdf = async () => {
    await handleSave();
    try {
      const res = await fetch(`${API}/invoices/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (!res.ok) { toast.error('Failed to load PDF for printing'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        toast.error('Pop-up blocked — allow pop-ups for this site to use Print.');
        URL.revokeObjectURL(url);
        return;
      }
      const tryPrint = () => { try { win.focus(); win.print(); } catch { /* ignore */ } };
      win.addEventListener('load', () => setTimeout(tryPrint, 400));
      setTimeout(() => { if (!win.closed) tryPrint(); }, 1500);
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    } catch { toast.error('Failed to print PDF'); }
  };

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/invoices/${id}/pdf?lang=${editorLang}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      }
    } catch { /* ignore */ }
    finally { setPreviewLoading(false); }
  }, [id, editorLang]);

  const togglePreview = async () => {
    if (!showPreview) {
      await handleSave();
      setShowPreview(true);
      loadPreview();
    } else {
      setShowPreview(false);
    }
  };

  // Cleanup blob on unmount
  useEffect(() => { return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }; }, [pdfUrl]);

  // Refresh preview when the editor language flips while preview is open
  useEffect(() => { if (showPreview) loadPreview(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [editorLang]);

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      await handleSave();
      const srcLang = editorLang === 'fr' ? 'fr' : 'en';
      const tgtLang = srcLang === 'fr' ? 'en' : 'fr';
      const res = await fetch(`${API}/ai/translate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ invoice_id: id, source_lang: srcLang, target_lang: tgtLang }),
      });
      if (res.ok) {
        const data = await res.json();
        delete data._cache_stats;
        setInvoice(prev => ({ ...prev, ...data }));
        // Translate updates the server-side document directly, so what we
        // have on the client now matches the DB — clear the dirty flag.
        setHasUnsavedChanges(false);
        toast.success(`Translated to ${tgtLang === 'fr' ? 'French' : 'English'}`);
        if (showPreview) loadPreview();
      } else {
        let detail = 'Translation failed';
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        toast.error(detail);
      }
    } catch { toast.error('Translation request failed.'); }
    finally { setTranslating(false); }
  };

  // Backdate this invoice. Server recomputes the INV-YYYY-MMDD-NN number
  // by counting existing invoices on that day and assigning the next slot.
  const handleBackdate = async (yyyyMmDd) => {
    if (!id || !yyyyMmDd) return;
    try {
      const res = await fetch(`${API}/invoices/${id}/backdate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: yyyyMmDd }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Could not change date');
        return;
      }
      const updated = await res.json();
      setInvoice(prev => ({
        ...prev,
        invoice_number: updated.invoice_number,
        created_at: updated.created_at,
        updated_at: updated.updated_at,
      }));
      toast.success(`Renumbered to ${updated.invoice_number}`);
    } catch {
      toast.error('Could not change date');
    }
  };

  const openEmailDialog = () => {
    const isFr = editorLang === 'fr';
    setEmailTo(invoice.client?.email || '');
    setEmailSubject(isFr
      ? `Facture ${invoice.invoice_number} — Kenscho Design`
      : `Invoice ${invoice.invoice_number} — Kenscho Design`);
    const fname = (invoice.client?.first_name || (invoice.client?.name || '').split(' ')[0] || '').trim();
    setEmailMessage(isFr
      ? `Bonjour ${fname},\n\nVeuillez trouver ci-joint la facture ${invoice.invoice_number}. Le paiement est dû à la réception.\n\nCordialement,\nKenscho Design`
      : `Hi ${fname},\n\nPlease find attached invoice ${invoice.invoice_number}. Payment is due on receipt.\n\nBest regards,\nKenscho Design`);
    setShowEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo) { toast.error('Recipient email required'); return; }
    setEmailSending(true);
    try {
      await handleSave();
      const res = await fetch(`${API}/invoices/${id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to_email: emailTo,
          subject: emailSubject,
          message: emailMessage,
          lang: editorLang,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Email sent');
        setInvoice(prev => ({ ...prev, status: 'sent' }));
        setShowEmailDialog(false);
      } else {
        let detail = 'Failed to send email';
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        toast.error(detail, { duration: 7000 });
      }
    } catch { toast.error('Failed to send email'); }
    finally { setEmailSending(false); }
  };

  // ── Unsaved-changes guard ─────────────────────────────────────────
  // Mirrors the EstimateEditor pattern: warn on browser tab close, and
  // intercept any in-app navigation through NavGuardContext.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!registerGuard) return undefined;
    return registerGuard(() => {
      if (!hasUnsavedChanges) return Promise.resolve(true);
      // Open the dialog and wait for the user's choice. The leaveResolverRef
      // is fulfilled by Stay / Discard / Save & Leave.
      setShowLeaveDialog(true);
      return new Promise((resolve) => { leaveResolverRef.current = resolve; });
    });
  }, [registerGuard, hasUnsavedChanges]);

  const tryNavigate = (to) => {
    if (!hasUnsavedChanges) { navigate(to); return; }
    setPendingNavigation(to);
    setShowLeaveDialog(true);
  };

  const handleLeaveCancel = () => {
    setShowLeaveDialog(false);
    setPendingNavigation(null);
    if (leaveResolverRef.current) { leaveResolverRef.current(false); leaveResolverRef.current = null; }
  };

  const handleLeaveDiscard = () => {
    setShowLeaveDialog(false);
    setHasUnsavedChanges(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (leaveResolverRef.current) { leaveResolverRef.current(true); leaveResolverRef.current = null; }
    if (target) navigate(target);
  };

  const handleLeaveSave = async () => {
    const ok = await handleSave();
    if (!ok) return;
    setShowLeaveDialog(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (leaveResolverRef.current) { leaveResolverRef.current(true); leaveResolverRef.current = null; }
    if (target) navigate(target);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!invoice) return null;

  const subtotal = (currentSections || []).reduce(
    (acc, s) => acc + (s.items || []).reduce((sa, it) => sa + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0),
    0
  );
  const gst = subtotal * (invoice.tax_gst || 0) / 100;
  const qst = subtotal * (invoice.tax_qst || 0) / 100;
  const total = subtotal + gst + qst;
  // Itemized payments; legacy deposit_paid shows as one synthetic row and
  // materializes into the payments array on first edit.
  const payments = (invoice.payments && invoice.payments.length)
    ? invoice.payments
    : (Number(invoice.deposit_paid) > 0
        ? [{ id: 'legacy_deposit', date: '', amount: Number(invoice.deposit_paid), method: 'other' }]
        : []);
  const handlePaymentsChange = (arr) => {
    setInvoice(prev => ({ ...prev, payments: arr, deposit_paid: 0 }));
    setHasUnsavedChanges(true);
  };
  const depositPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balanceDue = total - depositPaid;

  return (
    <div className="animate-fade-in" data-testid="invoice-editor">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => tryNavigate('/invoices')} data-testid="invoice-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Invoices
          </Button>
          <div>
            <h1 className="text-2xl tracking-tight font-semibold text-[#1A1D1C] flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
              <Receipt className="w-5 h-5 text-[#2D6B68]" />
              {invoice.invoice_number}
              {hasUnsavedChanges && (
                <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold" data-testid="invoice-unsaved-pill">
                  Unsaved
                </span>
              )}
            </h1>
            {invoice.parent_estimate_number && (
              <p className="text-xs text-[#6B7A73]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                From estimate {invoice.parent_estimate_number}
              </p>
            )}
            <div className="mt-1">
              <DatePickerButton
                value={invoice.created_at}
                onChange={handleBackdate}
                testId="invoice-date"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Language toggle */}
          <div className="flex items-center bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => setEditorLang('en')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${editorLang === 'en' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:bg-[#F5F6F5]'}`}
              data-testid="invoice-lang-en"
            >EN</button>
            <button
              type="button"
              onClick={() => setEditorLang('fr')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${editorLang === 'fr' ? 'bg-[#2D6B68] text-white' : 'text-[#6B7A73] hover:bg-[#F5F6F5]'}`}
              data-testid="invoice-lang-fr"
            >FR</button>
          </div>

          <Select value={invoice.status} onValueChange={v => updateField('status', v)}>
            <SelectTrigger className="w-28 bg-white border-[#E6EBE8] rounded-md text-sm" data-testid="invoice-editor-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={handleTranslate} disabled={translating} variant="outline" className="border-[#E6EBE8]" data-testid="invoice-translate-btn">
            <Languages className="w-4 h-4 mr-2" />
            {translating ? 'Syncing…' : (editorLang === 'fr' ? 'Sync EN' : 'Sync FR')}
          </Button>

          <Button onClick={togglePreview} variant={showPreview ? 'default' : 'outline'} className={showPreview ? 'bg-[#2D6B68] hover:bg-[#235552] text-white' : 'border-[#E6EBE8]'} data-testid="invoice-preview-btn">
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Close Preview' : 'Preview'}
          </Button>

          <Button onClick={openEmailDialog} variant="outline" className="border-[#E6EBE8]" data-testid="invoice-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Email
          </Button>

          <Button onClick={handleSave} disabled={saving} className="bg-[#2D6B68] hover:bg-[#235552] text-white rounded-md" data-testid="invoice-save-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={handleExportPdf} variant="outline" className="border-[#E6EBE8]" data-testid="invoice-export-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button onClick={handlePrintPdf} variant="outline" className="border-[#E6EBE8]" data-testid="invoice-print-pdf-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className={`flex gap-6 ${showPreview ? '' : ''}`}>
        <div className={`${showPreview ? 'w-1/2' : 'w-full'} transition-all duration-300`}>
          <div className="grid grid-cols-1 gap-6" style={{ gridTemplateColumns: showPreview ? '1fr' : '1fr 280px' }}>
            <div className="space-y-6">
              {/* Client + Title */}
              <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Invoice Info</h2>
              <div className="w-72">
                <ClientPicker
                  currentName={invoice.client?.company || invoice.client?.name || ''}
                  onSelect={(c) => {
                    const parts = (c.name || '').trim().split(' ');
                    setInvoice(prev => ({
                      ...prev,
                      client: {
                        name: c.name || '',
                        first_name: c.first_name || parts[0] || '',
                        last_name: c.last_name || parts.slice(1).join(' ') || '',
                        company: c.company || '',
                        email: c.email || '',
                        phone: c.phone || '',
                        address1: c.address1 || '',
                        address2: c.address2 || '',
                        postal_code: c.postal_code || '',
                      },
                    }));
                    toast.success(`Loaded ${c.company || c.name}`);
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Title</Label>
                <Input value={currentTitle} onChange={e => updateLangField('title', e.target.value)} className="text-sm" data-testid="invoice-title-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Company <span className="text-[#9BA5A0] font-normal">(optional)</span></Label>
                <Input value={invoice.client?.company || ''} onChange={e => updateClient('company', e.target.value)} className="text-sm" data-testid="invoice-client-company" />
              </div>
              <div>
                <Label className="text-xs">First name</Label>
                <Input
                  value={invoice.client?.first_name || ''}
                  onChange={e => {
                    updateClient('first_name', e.target.value);
                    updateClient('name', `${e.target.value} ${invoice.client?.last_name || ''}`.trim());
                  }}
                  className="text-sm"
                  data-testid="invoice-client-first-name"
                  placeholder="First name"
                />
              </div>
              <div>
                <Label className="text-xs">Last name</Label>
                <Input
                  value={invoice.client?.last_name || ''}
                  onChange={e => {
                    updateClient('last_name', e.target.value);
                    updateClient('name', `${invoice.client?.first_name || ''} ${e.target.value}`.trim());
                  }}
                  className="text-sm"
                  data-testid="invoice-client-last-name"
                  placeholder="Last name"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={invoice.client?.email || ''} onChange={e => updateClient('email', e.target.value)} className="text-sm" data-testid="invoice-client-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={invoice.client?.phone || ''} onChange={e => updateClient('phone', e.target.value)} className="text-sm" data-testid="invoice-client-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Address line 1</Label>
                <Input value={invoice.client?.address1 || ''} onChange={e => updateClient('address1', e.target.value)} className="text-sm" data-testid="invoice-client-address1" placeholder="Street address" />
              </div>
              <div>
                <Label className="text-xs">City, Province</Label>
                <Input value={invoice.client?.address2 || ''} onChange={e => updateClient('address2', e.target.value)} className="text-sm" data-testid="invoice-client-address2" placeholder="e.g. Saint-Calixte, QC" />
              </div>
              <div>
                <Label className="text-xs">Postal code</Label>
                <Input value={invoice.client?.postal_code || ''} onChange={e => updateClient('postal_code', e.target.value)} className="text-sm" data-testid="invoice-client-postal" placeholder="J0K 1Z0" />
              </div>
            </div>
          </div>

          {/* Sections */}
          {/* Project Scope — bullet list, hidden from PDF when empty */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">Project Scope</h2>
                <p className="text-[10px] text-[#6B7A73] mt-1">Optional. Shows on the PDF beside &quot;Prepared for&quot;. Leave empty to hide entirely.</p>
              </div>
              <Button variant="ghost" onClick={addBullet} className="text-xs text-[#2D6B68]" data-testid="invoice-add-bullet-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Point
              </Button>
            </div>
            <SortableList
              items={currentBullets}
              onReorder={(newOrder) => { updateField(bulletsKey, newOrder); }}
              getId={(_, i) => `inv-bullet-${i}`}
              renderItem={(bullet, i) => (
                <div className="flex items-start gap-2 mb-2">
                  <AutoTextarea
                    value={bullet}
                    onChange={e => updateBullet(i, e.target.value)}
                    minRows={1}
                    className="flex-1 bg-white border-[#E6EBE8] rounded-md text-sm"
                    placeholder="e.g. Final billing for completed bathroom renovation"
                    data-testid={`invoice-bullet-${i}`}
                  />
                  <button onClick={() => removeBullet(i)} className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors" data-testid={`invoice-remove-bullet-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            />
            {currentBullets.length === 0 && (
              <p className="text-xs text-[#9BA5A0] italic">No scope points — this section will be hidden on the PDF.</p>
            )}
          </div>

          {/* Sections — drag to reorder, click chevron to collapse */}
          <SortableList
            items={currentSections}
            onReorder={(newOrder) => { updateField(sectionsKey, newOrder); }}
            getId={(_, i) => `inv-section-${i}`}
            renderItem={(section, sIdx) => {
              const secTotal = (section.items || []).reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
              const expanded = expandedSections[sIdx];
              return (
                <div className="bg-white border border-[#E6EBE8] rounded-md mb-4" data-testid={`invoice-section-${sIdx}`}>
                  <div className="px-6 py-4 border-b border-[#E6EBE8] flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => setExpandedSections(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}
                        className="text-[#6B7A73] hover:text-[#1A1D1C] transition-colors"
                        data-testid={`invoice-section-toggle-${sIdx}`}
                      >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <Input
                        value={section.title || ''}
                        onChange={e => updateSectionTitle(sIdx, e.target.value)}
                        className="border-none bg-transparent p-0 h-auto focus:ring-0 text-sm font-medium text-[#1A1D1C]"
                        placeholder="Section title"
                        data-testid={`invoice-section-title-${sIdx}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-[#1A1D1C] bg-[#F5F6F5] px-2 py-1 rounded">
                        ${secTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {(currentSections || []).length > 1 && (
                        <button onClick={() => removeSection(sIdx)} className="p-1 text-[#6B7A73] hover:text-red-500" data-testid={`invoice-remove-section-${sIdx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {expanded && (
                    <div className="p-4">
                      <div className="grid grid-cols-12 gap-2 bg-[#F9F9F8] border-b border-[#E6EBE8] py-2 px-2 mb-2">
                        <span className="col-span-7 text-xs uppercase tracking-wider text-[#6B7A73] font-semibold">Description</span>
                        <span className="col-span-1 text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-center">Qty</span>
                        <span className="col-span-2 text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-center">Unit Price</span>
                        <span className="col-span-1 text-xs uppercase tracking-wider text-[#6B7A73] font-semibold text-right">Total</span>
                        <span className="col-span-1"></span>
                      </div>
                      <SortableList
                        items={section.items || []}
                        onReorder={(newItems) => {
                          // Write the reordered items back into the same section
                          // of the active language slot.
                          setInvoice(prev => {
                            const sections = [...(prev[sectionsKey] || prev.sections || [])];
                            sections[sIdx] = { ...sections[sIdx], items: newItems };
                            return { ...prev, [sectionsKey]: sections };
                          });
                          setHasUnsavedChanges(true);
                        }}
                        getId={(_, iIdx) => `inv-item-${sIdx}-${iIdx}`}
                        renderItem={(item, iIdx) => (
                          <div className="grid grid-cols-12 gap-2 items-start py-2 px-2" data-testid={`invoice-item-${sIdx}-${iIdx}`}>
                            <AutoTextarea
                              value={item.description || ''}
                              onChange={e => updateItem(sIdx, iIdx, 'description', e.target.value)}
                              placeholder="Description"
                              className="col-span-7 text-sm"
                              minRows={1}
                              data-testid={`invoice-item-desc-${sIdx}-${iIdx}`}
                            />
                            <Input
                              type="number" step="0.01"
                              value={item.qty || ''}
                              onChange={e => updateItem(sIdx, iIdx, 'qty', Number(e.target.value) || 0)}
                              placeholder="Qty"
                              className="col-span-1 text-sm text-right"
                              data-testid={`invoice-item-qty-${sIdx}-${iIdx}`}
                            />
                            <Input
                              type="number" step="0.01"
                              value={item.unit_price || ''}
                              onChange={e => updateItem(sIdx, iIdx, 'unit_price', Number(e.target.value) || 0)}
                              placeholder="Unit price"
                              className="col-span-2 text-sm text-right"
                              data-testid={`invoice-item-price-${sIdx}-${iIdx}`}
                            />
                            <div className="col-span-1 text-sm text-right py-2 font-mono text-[#1A1D1C]">
                              ${((Number(item.qty) || 0) * (Number(item.unit_price) || 0)).toFixed(2)}
                            </div>
                            <button onClick={() => removeItem(sIdx, iIdx)} className="col-span-1 p-2 text-[#6B7A73] hover:text-red-500 self-start" data-testid={`invoice-remove-item-${sIdx}-${iIdx}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      />
                      <Button size="sm" variant="ghost" onClick={() => addItem(sIdx)} className="text-[#2D6B68] hover:bg-[#F0F3F1] mt-2" data-testid={`invoice-add-item-${sIdx}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                      </Button>
                      {/* Section-level Notes — individual rows, same as estimates */}
                      <div className="mt-4 pt-3 border-t border-[#F0F3F1]">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">
                            Notes for this section <span className="normal-case lowercase tracking-normal text-[10px] text-[#9BA5A0]">(leave empty to hide on PDF)</span>
                          </Label>
                          <Button variant="ghost" onClick={() => addContingency(sIdx)} className="text-xs text-[#6B7A73] h-7" data-testid={`invoice-add-contingency-${sIdx}`}>
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <SortableList
                            items={section.contingencies || []}
                            onReorder={(newOrder) => updateSectionConts(sIdx, newOrder)}
                            getId={(_, cIdx) => `inv-cont-${sIdx}-${cIdx}`}
                            renderItem={(cont, cIdx) => (
                              <div className="flex items-start gap-2">
                                <AutoTextarea
                                  value={cont}
                                  onChange={(e) => updateContingency(sIdx, cIdx, e.target.value)}
                                  minRows={1}
                                  className="flex-1 bg-[#F9F9F8] border-[#E6EBE8] rounded-md text-xs"
                                  placeholder="Note..."
                                  style={{ minHeight: '38px' }}
                                  data-testid={`invoice-contingency-${sIdx}-${cIdx}`}
                                />
                                <button onClick={() => removeContingency(sIdx, cIdx)} className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors" data-testid={`invoice-remove-contingency-${sIdx}-${cIdx}`}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
          <Button variant="outline" onClick={addSection} className="border-dashed border-[#E6EBE8] w-full" data-testid="invoice-add-section-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Section
          </Button>

          {/* General Provisions — same style as the estimate editor */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6" data-testid="invoice-general-provisions">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">General Provisions</h2>
              <div className="flex items-center gap-1">
                <Button variant="ghost" onClick={() => setShowLibrary(true)} className="text-xs text-[#2D6B68]" data-testid="invoice-open-library-btn">
                  <BookOpen className="w-3.5 h-3.5 mr-1" /> Library
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { updateField(provisionsKey, [...currentProvisions, '']); }}
                  className="text-xs text-[#2D6B68]"
                  data-testid="invoice-add-provision-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {currentProvisions.length === 0 ? (
                <p className="text-xs text-[#6B7A73]">No general provisions — this section will be hidden on the PDF.</p>
              ) : (
                <SortableList
                  items={currentProvisions}
                  onReorder={(newOrder) => updateField(provisionsKey, newOrder)}
                  getId={(_, i) => `inv-prov-${i}`}
                  renderItem={(prov, i) => (
                    <div className="flex items-start gap-2">
                      <AutoTextarea
                        value={prov}
                        onChange={e => {
                          const arr = [...currentProvisions];
                          arr[i] = e.target.value;
                          updateField(provisionsKey, arr);
                        }}
                        minRows={1}
                        className="flex-1 bg-white border-[#E6EBE8] rounded-md focus:border-[#2D6B68] text-sm"
                        placeholder="e.g., Payment due upon completion. 25% deposit required."
                        style={{ minHeight: '38px' }}
                        data-testid={`invoice-provision-${i}`}
                      />
                      <button
                        onClick={() => updateField(provisionsKey, currentProvisions.filter((_, j) => j !== i))}
                        className="mt-2 p-1 text-[#6B7A73] hover:text-red-500 transition-colors"
                        data-testid={`invoice-remove-provision-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                />
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
            <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Notes</h2>
            <AutoTextarea
              value={currentNotes}
              onChange={e => updateLangField('notes', e.target.value)}
              placeholder={editorLang === 'fr' ? 'Notes internes ou pour le client' : 'Internal or client-facing notes'}
              minRows={3}
              data-testid="invoice-notes"
            />
          </div>

          {/* CO references */}
          {(invoice.accepted_co_refs || []).length > 0 && (
            <div className="bg-white border border-[#E6EBE8] rounded-md p-6">
              <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-3">Includes Change Orders</h2>
              <ul className="text-xs text-[#1A1D1C] space-y-1">
                {invoice.accepted_co_refs.map((co, i) => (
                  <li key={i} className="flex justify-between">
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{co.change_order_number} — {co.title}</span>
                    <span className="font-mono">${(co.subtotal || 0).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar — Totals */}
        <div className="space-y-4">
          <div className="bg-white border border-[#E6EBE8] rounded-md p-5" data-testid="invoice-totals">
            <h3 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em] mb-4">Summary</h3>
            <div className="space-y-2.5 text-sm">
              {(currentSections || []).map((s, i) => {
                const title = (s.title || '').trim();
                const label = title ? `${i + 1} - ${title}` : `${i + 1} - Section`;
                const secTotal = (s.items || []).reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
                return (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="text-[#6B7A73] truncate mr-2" title={label}>{label}</span>
                    <span className="text-[#1A1D1C] font-medium whitespace-nowrap" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ${secTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                );
              })}
              <div className="border-t border-[#E6EBE8] pt-2.5 flex justify-between">
                <span className="text-[#6B7A73]">Subtotal</span>
                <span className="text-[#1A1D1C] font-semibold" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-[#6B7A73] text-xs">GST %</span>
                <Input type="number" step="0.001" value={invoice.tax_gst} onChange={e => updateField('tax_gst', Number(e.target.value) || 0)} className="w-20 h-7 text-xs text-right" data-testid="invoice-tax-gst" />
                <span className="font-mono text-[#1A1D1C]">${gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-[#6B7A73] text-xs">QST %</span>
                <Input type="number" step="0.001" value={invoice.tax_qst} onChange={e => updateField('tax_qst', Number(e.target.value) || 0)} className="w-20 h-7 text-xs text-right" data-testid="invoice-tax-qst" />
                <span className="font-mono text-[#1A1D1C]">${qst.toFixed(2)}</span>
              </div>
              <div className="border-t border-[#E6EBE8] pt-2.5 flex justify-between">
                <span className="font-bold text-[#1A1D1C]">Total</span>
                <span className="font-mono font-bold text-[#2D6B68]">${total.toFixed(2)}</span>
              </div>

              <div className="pt-2 border-t border-[#E6EBE8]">
                <PaymentsEditor
                  payments={payments}
                  onChange={handlePaymentsChange}
                  testIdPrefix="invoice-payments"
                />
              </div>
              {depositPaid > 0 && (
                <div className="bg-[#E5F0EE] border border-[#2D6B68] rounded-md p-2 mt-2" data-testid="invoice-balance-due">
                  <div className="flex justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider">Balance Due</span>
                    <span className="font-mono font-bold text-[#2D6B68]">${balanceDue.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E6EBE8] rounded-md p-5">
            <Label className="text-[10px] uppercase tracking-wider text-[#6B7A73]">Payment Terms</Label>
            <Input value={currentTerms} onChange={e => updateLangField('payment_terms', e.target.value)} className="text-sm mt-1" placeholder={editorLang === 'fr' ? 'Payable à la réception' : 'Due on receipt'} data-testid="invoice-payment-terms" />
          </div>
            </div>
          </div>
        </div>

        {showPreview && (
          <div className="w-1/2 sticky top-4 self-start" data-testid="invoice-preview-panel">
            <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
              <div className="px-4 py-2.5 border-b border-[#E6EBE8] flex items-center justify-between">
                <span className="text-xs font-semibold text-[#1A1D1C] uppercase tracking-wider">PDF Preview ({editorLang.toUpperCase()})</span>
                <Button onClick={loadPreview} disabled={previewLoading} variant="ghost" size="sm" className="h-7 text-xs" data-testid="invoice-preview-refresh">
                  <RefreshCw className={`w-3 h-3 mr-1 ${previewLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              {previewLoading && !pdfUrl ? (
                <div className="flex-1 flex items-center justify-center text-xs text-[#6B7A73]">Loading…</div>
              ) : pdfUrl ? (
                // PDF viewer URL params: hide the thumbnail sidebar
                // (`navpanes=0`, `pagemode=none`) and fit to width
                // (`view=FitH`). Chrome's built-in PDF viewer respects
                // these; other viewers ignore them gracefully.
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitH&pagemode=none`}
                  title="Invoice PDF preview"
                  className="flex-1 w-full"
                  data-testid="invoice-preview-iframe"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-xs text-[#6B7A73]">No preview yet</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Email dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
              <Mail className="w-5 h-5 text-[#2D6B68]" /> Send invoice ({editorLang.toUpperCase()})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">To</Label>
              <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="client@example.com" className="text-sm" data-testid="invoice-email-to" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="text-sm" data-testid="invoice-email-subject" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <AutoTextarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} minRows={6} className="text-sm" data-testid="invoice-email-message" />
            </div>
            <p className="text-[10px] text-[#6B7A73]">PDF in {editorLang === 'fr' ? 'French' : 'English'} will be attached automatically.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="border-[#E6EBE8]" data-testid="invoice-email-cancel">Cancel</Button>
            <Button onClick={handleSendEmail} disabled={emailSending || !emailTo} className="bg-[#2D6B68] hover:bg-[#235552] text-white" data-testid="invoice-email-send">
              <Mail className="w-4 h-4 mr-2" />{emailSending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contingency / provisions library picker (shared with estimates) */}
      <ContingencyLibraryDialog
        open={showLibrary}
        onOpenChange={setShowLibrary}
        initialChecked={new Set(currentProvisions || [])}
        onApply={(selected) => {
          updateField(provisionsKey, selected);
          setShowLibrary(false);
          toast.success(`${selected.length} provision${selected.length !== 1 ? 's' : ''} applied`);
        }}
      />

      {/* Unsaved-changes confirmation dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={(open) => { if (!open) handleLeaveCancel(); }}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8]" data-testid="invoice-leave-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Unsaved changes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7A73]">
            You have unsaved changes on this invoice. What would you like to do?
          </p>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={handleLeaveCancel} className="border-[#E6EBE8]" data-testid="invoice-leave-stay">
              Stay & keep editing
            </Button>
            <Button variant="outline" onClick={handleLeaveDiscard} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="invoice-leave-discard">
              Discard changes
            </Button>
            <Button onClick={handleLeaveSave} disabled={saving} className="bg-[#2D6B68] hover:bg-[#235552] text-white" data-testid="invoice-leave-save">
              {saving ? 'Saving…' : 'Save & leave'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
